import React, { useState } from 'react';
import { CSSObject } from '@emotion/react';
import { EventAdditionalQuery } from 'queries';
import { IAdditional, IQuestion } from 'types';
import {
  compareApplicationTypes,
  IEvent,
  getApplicationTypeLabel,
} from 'types';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { MdArrowBack } from 'react-icons/md';

dayjs.locale('ko');
const FORMAT = 'MM.DD(ddd) A HH:mm';

interface Props {
  event: IEvent;
  onBack: () => void;
}

function PreviewEnrollForm({ event, onBack }: Props) {
  const { data: additional } = EventAdditionalQuery.useGet(String(event.id));
  const questions = (additional as IAdditional | undefined)?.questions ?? [];

  const options = Object.keys(event.applicationTypeQuota || {})
    .sort(compareApplicationTypes)
    .map((key) => ({
      name: event.applicationTypeQuotaTitle?.[key]
        ? getApplicationTypeLabel(key, event.applicationTypeQuotaTitle)
        : `${getApplicationTypeLabel(key)} 봉사자`,
      value: key,
    }));

  const [applicationType, setApplicationType] = useState<string>(
    options[0]?.value ?? 'GENERAL',
  );
  const [consent, setConsent] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  return (
    <div css={pageWrap}>
      {/* 네비 */}
      <div css={navBar}>
        <button type="button" css={backBtn} onClick={onBack}>
          <MdArrowBack size={22} />
        </button>
        <span css={navTitle}>봉사 신청</span>
      </div>

      {/* 행사 정보 */}
      <div css={sectionCard}>
        <div css={eventInfoInner}>
          <h2 css={eventInfoTitle}>{event.title}</h2>
          <ul css={eventInfoList}>
            <li>
              <p>봉사 일시</p>
              <p>{dayjs(event.startDate).format(FORMAT)}</p>
            </li>
          </ul>
        </div>
      </div>

      {/* 봉사자 유형 (2개 이상일 때만) */}
      {options.length > 1 && (
        <div css={typeSection}>
          <h2 css={typeSectionTitle}>봉사자 유형을 선택해주세요</h2>
          <div css={typeList}>
            {options.map((opt) => (
              <div
                key={opt.value}
                css={typeItem(applicationType === opt.value)}
                onClick={() => setApplicationType(opt.value)}
              >
                <div css={radioCircle(applicationType === opt.value)} />
                <p css={typeLabel}>{opt.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 사전 설문 */}
      <div css={formSection}>
        {questions.length > 0 && <h2 css={formTitle}>사전 설문</h2>}
        <div css={questionList}>
          {questions.map((q) => (
            <QuestionCard key={q.id} question={q} />
          ))}

          {/* 초상권 동의 */}
          <div css={questionCard}>
            <label css={consentRow}>
              <div
                css={checkBox(consent)}
                onClick={() => setConsent(!consent)}
              />
              <p css={consentText}>초상권 활용 및 이용 동의(필수)</p>
            </label>
            <p css={consentDesc}>
              원활한 봉사 진행을 위해 사진 및 영상 촬영이 진행될 수 있습니다.
              초상권 활용에 동의하지 않으실 수 있으나, 이 경우 봉사활동 참여가
              어렵습니다.
            </p>
            <hr css={hrStyle} />
            <div css={infoToggleRow} onClick={() => setInfoOpen(!infoOpen)}>
              <p>활용 및 수집 목적 자세히 살펴보기</p>
              <span
                css={{
                  transform: infoOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                  display: 'inline-block',
                }}
              >
                ▾
              </span>
            </div>
            {infoOpen && (
              <ul css={infoList}>
                <li>
                  수집 목적: 연봉인상 및 협업 기관의 온/오프라인 홍보, 활동
                  기록, 결과 보고
                </li>
                <li>수집 항목: 촬영된 원본 및 편집된 2차 가공물</li>
                <li>
                  권리 귀속: 해당 자료의 소유권 및 저작권은 연봉인상 및 협업
                  기관에 귀속됩니다.
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      <div css={{ height: 32 }} />
    </div>
  );
}

/* ── 개별 질문 카드 ── */

function QuestionCard({ question }: { question: IQuestion }) {
  return (
    <div css={questionCard}>
      <h2 css={questionTitle}>
        {question.title}
        {question.isRequired && <span css={{ color: '#FD5C2B' }}>*</span>}
      </h2>
      {question.description && <p css={questionDesc}>{question.description}</p>}
      {question.type === 'SINGLE_CHOICE' && (
        <SingleChoice question={question} />
      )}
      {question.type === 'TEXT' && <TextInput question={question} />}
      {question.type === 'LINEAR_SCALE' && <LinearScale question={question} />}
      {question.type === 'MULTIPLE_CHOICE' && (
        <MultiChoice question={question} />
      )}
    </div>
  );
}

function SingleChoice({ question }: { question: IQuestion }) {
  const [current, setCurrent] = useState('');
  return (
    <div css={answerWrap}>
      {question.options?.map((opt) => (
        <div key={opt.id} css={radioRow} onClick={() => setCurrent(opt.value)}>
          <div css={radioCircle(current === opt.value)} />
          <p css={optionLabel}>{opt.value}</p>
        </div>
      ))}
    </div>
  );
}

function TextInput({ question }: { question: IQuestion }) {
  const [val, setVal] = useState('');
  return (
    <div css={answerWrap}>
      <input
        css={textInput}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="답변을 입력해주세요"
      />
    </div>
  );
}

function LinearScale({ question }: { question: IQuestion }) {
  const [current, setCurrent] = useState(-1);
  const { options } = question;
  if (!options?.length) return null;

  const min = Number(options[0]?.subValue ?? 1);
  const max = Number(options[options.length - 1]?.subValue ?? options.length);
  const count = Math.abs(max - min) + 1;

  return (
    <div css={answerWrap}>
      <div css={scaleBar}>
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            css={scaleItem(current === i + 1)}
            onClick={() => setCurrent(i + 1)}
          >
            {min + i}
          </div>
        ))}
      </div>
      <div css={scaleLabelRow}>
        {options.map((opt, i) => (
          <p key={i}>{opt.value}</p>
        ))}
      </div>
    </div>
  );
}

function MultiChoice({ question }: { question: IQuestion }) {
  const [checked, setChecked] = useState<string[]>([]);
  const [etcChecked, setEtcChecked] = useState(false);
  const [etcVal, setEtcVal] = useState('');

  const toggle = (val: string) =>
    setChecked((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );

  return (
    <div css={answerWrap}>
      {question.options?.map((opt) => (
        <div key={opt.id} css={checkRow} onClick={() => toggle(opt.value)}>
          <div css={checkBox(checked.includes(opt.value))} />
          <p css={optionLabel}>{opt.value}</p>
        </div>
      ))}
      <div css={checkRow} onClick={() => setEtcChecked(!etcChecked)}>
        <div css={checkBox(etcChecked)} />
        <p css={optionLabel}>기타</p>
      </div>
      {etcChecked && (
        <input
          css={textInput}
          value={etcVal}
          onChange={(e) => setEtcVal(e.target.value)}
          placeholder="답변을 입력해주세요"
        />
      )}
    </div>
  );
}

/* ── styles ── */

const pageWrap: CSSObject = {
  background: '#EAEAEC',
  minHeight: '100%',
};

const navBar: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 16px',
  background: '#fff',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const backBtn: CSSObject = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  color: '#2E2F39',
};

const navTitle: CSSObject = {
  fontSize: 16,
  fontWeight: 600,
  color: '#2E2F39',
};

/* EventInfo */
const sectionCard: CSSObject = {
  padding: 16,
  background: '#fff',
};

const eventInfoInner: CSSObject = {
  border: '1px solid #EAEAEC',
  borderRadius: 8,
  background: '#F7F7F8',
  padding: 16,
};

const eventInfoTitle: CSSObject = {
  fontSize: 16,
  fontWeight: 600,
  color: '#2E2F39',
  marginBottom: 24,
};

const eventInfoList: CSSObject = {
  '> li': {
    display: 'flex',
    padding: '4px 0',
    'p:first-of-type': {
      width: 60,
      fontSize: 14,
      fontWeight: 500,
      color: '#5F6069',
    },
    'p:last-of-type': {
      fontSize: 14,
      fontWeight: 500,
      color: '#2E2F39',
    },
  },
};

/* Application type */
const typeSection: CSSObject = {
  marginTop: 8,
  padding: '24px 16px',
  background: '#fff',
};

const typeSectionTitle: CSSObject = {
  fontSize: 16,
  fontWeight: 600,
  color: '#2E2F39',
  marginBottom: 24,
};

const typeList: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const typeItem = (selected: boolean): CSSObject => ({
  padding: 16,
  background: '#fff',
  borderRadius: 8,
  border: `1px solid ${selected ? '#2E2F39' : 'rgba(231,231,231,1)'}`,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
});

const typeLabel: CSSObject = {
  fontSize: 15,
  color: '#2E2F39',
};

/* Question form */
const formSection: CSSObject = {
  marginTop: 8,
  padding: '0 16px 16px',
  background: '#fff',
};

const formTitle: CSSObject = {
  fontSize: 16,
  fontWeight: 600,
  color: '#2E2F39',
  padding: '24px 0',
};

const questionList: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const questionCard: CSSObject = {
  border: '1px solid #EAEAEC',
  padding: 20,
  background: '#fff',
  borderRadius: 8,
};

const questionTitle: CSSObject = {
  fontSize: 15,
  fontWeight: 600,
  lineHeight: '21px',
  marginBottom: 6,
  color: '#2E2F39',
};

const questionDesc: CSSObject = {
  fontSize: 12,
  lineHeight: '18px',
  color: '#90929A',
};

const answerWrap: CSSObject = {
  marginTop: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

/* Radio */
const radioRow: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
};

const radioCircle = (active: boolean): CSSObject => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: `2px solid ${active ? '#2E2F39' : '#C4C6CA'}`,
  background: active ? '#2E2F39' : '#fff',
  flexShrink: 0,
  position: 'relative',
  '&::after': active
    ? {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: '#fff',
      }
    : {},
});

const optionLabel: CSSObject = {
  fontSize: 14,
  lineHeight: '21px',
  fontWeight: 400,
  color: '#2E2F39',
};

/* Checkbox */
const checkRow: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
};

const checkBox = (checked: boolean): CSSObject => ({
  width: 18,
  height: 18,
  borderRadius: 3,
  border: `2px solid ${checked ? '#2E2F39' : '#C4C6CA'}`,
  background: checked ? '#2E2F39' : '#fff',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&::after': checked
    ? {
        content: '"✓"',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
      }
    : {},
});

/* Text input */
const textInput: CSSObject = {
  width: '100%',
  outline: 'none',
  borderWidth: '0 0 1px',
  borderStyle: 'solid',
  borderColor: '#DCDDDF',
  fontSize: 15,
  lineHeight: '21px',
  padding: '10px 0',
  background: 'transparent',
  '::placeholder': { color: '#DCDDDF' },
  ':focus': { borderColor: '#2E2F39' },
};

/* Linear scale */
const scaleBar: CSSObject = {
  display: 'flex',
  flexDirection: 'row',
  border: '1px solid #EAEAEC',
  borderRadius: 4,
  height: 40,
  overflow: 'hidden',
  'div + div': { borderLeft: '1px solid #EAEAEC' },
};

const scaleItem = (selected: boolean): CSSObject => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: selected ? '#2E2F39' : '#fff',
  color: selected ? '#fff' : '#C4C6CA',
  cursor: 'pointer',
  fontSize: 10,
  fontWeight: 600,
});

const scaleLabelRow: CSSObject = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginTop: 8,
  fontSize: 12,
  lineHeight: '16px',
  color: '#90929A',
};

/* Consent */
const consentRow: CSSObject = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  cursor: 'pointer',
};

const consentText: CSSObject = {
  fontSize: 15,
  lineHeight: 1.4,
  fontWeight: 400,
  color: '#2E2F39',
};

const consentDesc: CSSObject = {
  marginTop: 10,
  fontSize: 12,
  lineHeight: 1.4,
  color: '#5F6069',
  wordBreak: 'keep-all',
};

const hrStyle: CSSObject = {
  margin: '12px 0',
  border: 0,
  borderTop: '1px solid #F7F7F8',
};

const infoToggleRow: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.4,
  color: '#5F6069',
  cursor: 'pointer',
};

const infoList: CSSObject = {
  marginTop: 8,
  '> li': {
    '::before': { content: '"-"', marginRight: 4 },
    display: 'flex',
    fontSize: 13,
    lineHeight: 1.45,
    color: '#777981',
  },
};

export default PreviewEnrollForm;
